// src/pages/Perfil.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldOff } from 'lucide-react';
import { ArrowLeft, Lock, Check, Upload, X, PenTool, ShieldCheck } from '../components/icons';
import { useNavigate } from 'react-router-dom';
import api, { authAPI } from '../services/api';
import { SignatureImage } from '../utils/signatureUtils';
import SignatureDrawer from '../components/SignatureDrawer';

export default function Perfil() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [message, setMessage] = useState('');
    const [signatureFile, setSignatureFile] = useState(null);
    const [uploadingSignature, setUploadingSignature] = useState(false);
    const [currentSignature, setCurrentSignature] = useState(null);
    const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);

    // MFA (verificación en dos pasos)
    const [mfaEnabled, setMfaEnabled] = useState(null); // null = aún no se sabe
    const [showMfaSetupModal, setShowMfaSetupModal] = useState(false);
    const [showMfaDisableModal, setShowMfaDisableModal] = useState(false);
    const [mfaSetupData, setMfaSetupData] = useState(null); // { secreto, otpauthUrl, qrDataUrl }
    const [mfaSetupCode, setMfaSetupCode] = useState('');
    const [mfaDisablePassword, setMfaDisablePassword] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaMessage, setMfaMessage] = useState('');

    const cargarEstadoMfa = async () => {
        try {
            const { data } = await authAPI.mfaStatus();
            setMfaEnabled(!!data.activado);
        } catch (error) {
            console.error('Error al cargar estado de MFA:', error);
            setMfaEnabled(false);
        }
    };

    const handleAbrirMfaSetup = async () => {
        setMfaMessage('');
        setMfaLoading(true);
        try {
            const { data } = await authAPI.mfaSetup();
            setMfaSetupData(data);
            setShowMfaSetupModal(true);
        } catch (error) {
            console.error('Error al iniciar MFA:', error);
            setMessage(error.response?.data?.message || 'No se pudo iniciar la configuración de MFA');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleConfirmarMfaSetup = async () => {
        if (!mfaSetupCode.trim()) {
            setMfaMessage('Ingresa el código de tu app de autenticación');
            return;
        }
        setMfaLoading(true);
        setMfaMessage('');
        try {
            await authAPI.mfaConfirmSetup(mfaSetupCode.trim());
            setMfaEnabled(true);
            setShowMfaSetupModal(false);
            setMfaSetupData(null);
            setMfaSetupCode('');
            setMessage('Verificación en dos pasos activada correctamente');
        } catch (error) {
            console.error('Error al confirmar MFA:', error);
            setMfaMessage(error.response?.data?.message || 'Código incorrecto');
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDesactivarMfa = async () => {
        if (!mfaDisablePassword.trim()) {
            setMfaMessage('Ingresa tu contraseña actual');
            return;
        }
        setMfaLoading(true);
        setMfaMessage('');
        try {
            await authAPI.mfaDisable(mfaDisablePassword.trim());
            setMfaEnabled(false);
            setShowMfaDisableModal(false);
            setMfaDisablePassword('');
            setMessage('Verificación en dos pasos desactivada');
        } catch (error) {
            console.error('Error al desactivar MFA:', error);
            setMfaMessage(error.response?.data?.message || 'Contraseña incorrecta');
        } finally {
            setMfaLoading(false);
        }
    };

    useEffect(() => {
        const loadCurrentUser = async () => {
            try {
                const response = await api.get('/auth/me');
                setProfileData(response.data);
                setCurrentSignature(response.data.signaturePath);
            } catch (error) {
                console.error('Error al cargar datos del usuario:', error);
            }
        };
        loadCurrentUser();
        cargarEstadoMfa();
    }, []);

    const handleSignatureFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                setSignatureFile(file);
            } else {
                setMessage('Solo se permiten archivos de imagen');
            }
        }
    };

    const handleUploadSignature = async () => {
        if (!signatureFile) {
            setMessage('Por favor selecciona un archivo de imagen');
            return;
        }

        setUploadingSignature(true);
        setMessage('');

        try {
            const formData = new FormData();
            formData.append('signature', signatureFile);

            const response = await api.post('/auth/upload-signature', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setMessage('Firma subida exitosamente');
            setCurrentSignature(response.data.signaturePath);
            setSignatureFile(null);

            // Limpiar el input de archivo
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) fileInput.value = '';

        } catch (error) {
            console.error('Error al subir firma:', error);
            setMessage('Error al subir la firma. Inténtalo de nuevo.');
        } finally {
            setUploadingSignature(false);
        }
    };

    const handleRemoveSignature = async () => {
        setUploadingSignature(true);
        setMessage('');

        try {
            await api.delete('/auth/remove-signature');
            setMessage('Firma eliminada exitosamente');
            setCurrentSignature(null);
        } catch (error) {
            console.error('Error al eliminar firma:', error);
            setMessage('Error al eliminar la firma. Inténtalo de nuevo.');
        } finally {
            setUploadingSignature(false);
        }
    };

    const handleSaveDrawnSignature = async (file) => {
        setUploadingSignature(true);
        setMessage('');

        try {
            const formData = new FormData();
            formData.append('signature', file);

            const response = await api.post('/auth/upload-signature', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setMessage('Firma creada y guardada exitosamente');
            setCurrentSignature(response.data.signaturePath);
            setShowSignatureDrawer(false);
        } catch (error) {
            console.error('Error al guardar firma dibujada:', error);
            setMessage('Error al guardar la firma dibujada. Inténtalo de nuevo.');
        } finally {
            setUploadingSignature(false);
        }
    };

    const p = profileData || {};
    const nombreCompleto = (p.nombre || user?.nombre) && (p.apellido || user?.apellido)
        ? `${p.nombre || user.nombre} ${p.apellido || user.apellido}`
        : user?.username || 'Usuario';
    const iniciales = user?.nombre && user?.apellido
        ? `${user.nombre[0]}${user.apellido[0]}`.toUpperCase()
        : user?.username?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
    const rolTexto = p.role || user?.role || 'usuario';

    const Dato = ({ label, value, capitalize }) => (
        <div className="min-w-0">
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className={`mt-1 text-sm text-gray-900 dark:text-gray-100 break-words ${capitalize ? 'capitalize' : ''}`}>
                {value || <span className="text-gray-400 dark:text-gray-500">No disponible</span>}
            </dd>
        </div>
    );

    const Seccion = ({ titulo, descripcion, children }) => (
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">{titulo}</h2>
                {descripcion && <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{descripcion}</p>}
            </div>
            <div className="px-6 py-5">{children}</div>
        </section>
    );

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            {/* Encabezado */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
                    aria-label="Volver"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Mi perfil</h1>
            </div>

            {/* Mensaje de estado */}
            {message && (
                <div className={`px-4 py-3 rounded-lg flex items-center gap-2 text-sm border ${message.includes('Error') || message.includes('incorrecta')
                    ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                    : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                    }`}>
                    <Check className="w-4 h-4 shrink-0" />
                    <span>{message}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Columna izquierda: identidad y firma */}
                <div className="space-y-6">
                <aside className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 flex flex-col items-center text-center">
                    <div className="h-24 w-24 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-3xl font-medium text-white tracking-wide">{iniciales}</span>
                    </div>
                    <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{nombreCompleto}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 break-all">{p.email || user?.email}</p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 capitalize">
                            {rolTexto}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                            Activo
                        </span>
                    </div>
                    {(p.cargo || p.empresa) && (
                        <div className="mt-5 pt-5 w-full border-t border-gray-100 dark:border-gray-700 text-sm">
                            {p.cargo && <p className="text-gray-700 dark:text-gray-300">{p.cargo}</p>}
                            {p.empresa && <p className="mt-0.5 text-gray-500 dark:text-gray-400">{p.empresa}</p>}
                        </div>
                    )}
                </aside>

                <Seccion titulo="Firma digital" descripcion="Se usa para firmar actas y otros documentos.">
                    <div className="space-y-4">
                        {currentSignature && (
                            <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Firma actual</p>
                                    <SignatureImage
                                        signaturePath={currentSignature}
                                        alt="Firma actual"
                                        className="max-h-16 bg-white p-2 border border-gray-200 rounded"
                                        onError={(error) => {
                                            console.error('Error cargando firma:', error);
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={handleRemoveSignature}
                                    disabled={uploadingSignature}
                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 shrink-0 transition-colors"
                                >
                                    <X className="w-3.5 h-3.5 mr-1.5" />
                                    Eliminar
                                </button>
                            </div>
                        )}
                        <div className="flex flex-col gap-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Dibuja tu firma o sube una foto; el fondo se quita automáticamente para generar un PNG transparente.
                            </p>
                            <button
                                onClick={() => setShowSignatureDrawer(true)}
                                disabled={uploadingSignature}
                                className="inline-flex w-full items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 shrink-0 transition-colors"
                            >
                                <PenTool className="w-4 h-4 mr-2" />
                                {currentSignature ? 'Cambiar firma' : 'Configurar firma'}
                            </button>
                        </div>
                    </div>
                </Seccion>
                </div>

                {/* Columna derecha: secciones */}
                <div className="lg:col-span-2 space-y-6">
                    <Seccion titulo="Información personal" descripcion="Estos datos provienen de la nómina y no se editan desde aquí.">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                            <Dato label="Nombre" value={p.nombre || user?.nombre} />
                            <Dato label="Apellido" value={p.apellido || user?.apellido} />
                            <Dato label="Correo electrónico" value={p.email || user?.email} />
                            <Dato label="Rol en el sistema" value={rolTexto} capitalize />
                            <Dato label="Departamento" value={p.departamento} />
                            <Dato label="Empresa" value={p.empresa} />
                            <Dato label="Cargo" value={p.cargo} />
                            <Dato label="ID de usuario" value={String(p.id || user?.id || '')} />
                        </dl>
                    </Seccion>

                    <Seccion titulo="Seguridad" descripcion="Protege el acceso a tu cuenta.">
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            <div className="pb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    {mfaEnabled ? (
                                        <ShieldCheck className="w-5 h-5 mt-0.5 text-green-600 dark:text-green-400 shrink-0" />
                                    ) : (
                                        <ShieldOff className="w-5 h-5 mt-0.5 text-gray-400 shrink-0" />
                                    )}
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            Verificación en dos pasos
                                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${mfaEnabled
                                                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                                {mfaEnabled === null ? 'Consultando…' : mfaEnabled ? 'Activada' : 'Desactivada'}
                                            </span>
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Usa una app de autenticación, como Microsoft Authenticator, al iniciar sesión con correo y contraseña.
                                        </p>
                                    </div>
                                </div>
                                {mfaEnabled === false && (
                                    <button
                                        onClick={handleAbrirMfaSetup}
                                        disabled={mfaLoading}
                                        className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 shrink-0 transition-colors"
                                    >
                                        Activar
                                    </button>
                                )}
                                {mfaEnabled === true && (
                                    <button
                                        onClick={() => { setMfaMessage(''); setMfaDisablePassword(''); setShowMfaDisableModal(true); }}
                                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0 transition-colors"
                                    >
                                        Desactivar
                                    </button>
                                )}
                            </div>
                            <div className="pt-5 flex items-start gap-3">
                                <Lock className="w-5 h-5 mt-0.5 text-gray-400 shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Contraseña</p>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        El acceso a HelpDesk usa el inicio de sesión central. Si ingresas con Microsoft, cambia tu contraseña desde tu cuenta de Microsoft; si usas una cuenta local, solicítalo a TI.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Seccion>
                </div>
            </div>

            {/* Modal para crear/subir firma */}
            <SignatureDrawer
                isOpen={showSignatureDrawer}
                onClose={() => setShowSignatureDrawer(false)}
                onSave={handleSaveDrawnSignature}
                title="Configurar tu firma digital"
            />

            {/* Modal para activar MFA (setup: QR + confirmación) */}
            {showMfaSetupModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowMfaSetupModal(false);
                            setMfaSetupData(null);
                            setMfaSetupCode('');
                            setMfaMessage('');
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-sm">
                        <h3 className="text-lg font-medium mb-1 text-gray-900 dark:text-white">Activar verificación en dos pasos</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Escanea este código con Microsoft Authenticator (o cualquier app compatible con TOTP) y luego ingresa el código generado.
                        </p>

                        {mfaMessage && (
                            <div className="mb-4 p-3 rounded-lg text-sm bg-red-100 text-red-700 border border-red-200">
                                {mfaMessage}
                            </div>
                        )}

                        {mfaSetupData?.qrDataUrl && (
                            <div className="flex justify-center mb-4">
                                <img src={mfaSetupData.qrDataUrl} alt="Código QR para MFA" className="w-48 h-48 border border-gray-200 dark:border-gray-600 rounded-lg p-2 bg-white" />
                            </div>
                        )}

                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 text-center">
                            ¿No puedes escanear? Ingresa este código manualmente:
                            <br />
                            <span className="font-mono font-semibold text-gray-700 dark:text-gray-300">{mfaSetupData?.secreto}</span>
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Código de verificación
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                autoFocus
                                value={mfaSetupCode}
                                onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-center text-xl tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="000000"
                            />
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={handleConfirmarMfaSetup}
                                disabled={mfaLoading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {mfaLoading ? 'Verificando...' : 'Activar'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowMfaSetupModal(false);
                                    setMfaSetupData(null);
                                    setMfaSetupCode('');
                                    setMfaMessage('');
                                }}
                                disabled={mfaLoading}
                                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal para desactivar MFA (requiere contraseña actual) */}
            {showMfaDisableModal && createPortal(
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowMfaDisableModal(false);
                            setMfaDisablePassword('');
                            setMfaMessage('');
                        }
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-sm">
                        <h3 className="text-lg font-medium mb-1 text-gray-900 dark:text-white">Desactivar verificación en dos pasos</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Confirma tu contraseña actual para desactivar el segundo factor.
                        </p>

                        {mfaMessage && (
                            <div className="mb-4 p-3 rounded-lg text-sm bg-red-100 text-red-700 border border-red-200">
                                {mfaMessage}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Contraseña actual
                            </label>
                            <input
                                type="password"
                                autoFocus
                                value={mfaDisablePassword}
                                onChange={(e) => setMfaDisablePassword(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Ingresa tu contraseña"
                            />
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={handleDesactivarMfa}
                                disabled={mfaLoading}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {mfaLoading ? 'Desactivando...' : 'Desactivar'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowMfaDisableModal(false);
                                    setMfaDisablePassword('');
                                    setMfaMessage('');
                                }}
                                disabled={mfaLoading}
                                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}